<template>
    <div class="page container">
        <!-- Header -->
        <header class="header">
            <img src="@/assets/images/profile.png" class="avatar" alt="" />
            <span ref="featureSection"></span>
            <h1 class="tagline">Let’s work together.</h1>
            <div v-if="loader" class="loading">
                <lottie-player :src="require('@/assets/lottie/Preloader.json')" class="ticker-animation"
                    background="transparent" speed="1" style="width: 150px; height: 150px;" loop
                    autoplay></lottie-player>
            </div>
        </header>

        <!-- Loader -->

        <!-- Video + Grid -->
        <div v-if="!loader">
            <section v-if="selectedVideo">
                <div class="video-wrapper">
                    <!-- Vimeo player container -->
                    <div ref="playerContainer" class="featured-player"></div>

                    <!-- Transparent overlay to catch clicks -->
                    <div class="video-overlay" @click="onWrapperClick"></div>
                    <div class="timeline-wrapper">
                        <div class="timeline-wrapper" @click="onTrackClick" @touchend.prevent="onTrackClick">
                            <input ref="timeline" type="range" class="timeline" :min="0" :max="duration" step="0.1"
                                v-model="currentTime" @input="onSeek" :style="{ background: timelineBG }" />
                        </div>
                    </div>
                    <!-- Custom play/pause button with SVG icons -->
                    <button @click.stop="onPlayClick" @touchstart.prevent.stop="onPlayClick"
                        :class="['custom-play-btn', { visible: showButton }]">
                        <template v-if="!isPlaying">
                            <!-- Play Icon -->
                            <svg width="56" height="56" viewBox="0 0 56 56" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <circle cx="28" cy="28" r="28" fill="#F63E02" />
                                <path
                                    d="M21.3233 35.3212L21.4408 20.9658C21.4565 19.0452 23.56 17.8747 25.2004 18.8738L36.9727 26.0437C38.5582 27.0094 38.5542 29.3125 36.9654 30.2727L25.0756 37.4581C23.4196 38.4589 21.3075 37.2561 21.3233 35.3212Z"
                                    fill="white" />
                            </svg>
                        </template>
                        <template v-else>
                            <!-- Pause Icon -->
                            <svg width="56" height="56" viewBox="0 0 56 56" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <circle cx="28" cy="28" r="28" fill="#F63E02" />
                                <rect x="20" y="18" width="5" height="20" fill="white" />
                                <rect x="31" y="18" width="5" height="20" fill="white" />
                            </svg>
                        </template>
                    </button>
                </div>

                <h2 class="video-title">{{ selectedVideo.title }}</h2>
                <p class="video-description">{{ selectedVideo.description }}</p>
            </section>

            <section class="grid">
                <div v-for="vid in videos" :key="vid.id" class="grid-item" @click="selectVideo(vid)">
                    <img :src="vid.thumbnail" alt="Video thumbnail" />
                </div>
            </section>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick, onBeforeUnmount, computed } from 'vue'
import axios from 'axios'
import Player from '@vimeo/player'

const ACCESS_TOKEN = process.env.VUE_APP_ACCESS_TOKEN

const videos = ref([])
const selectedVideo = ref(null)
const loader = ref(true)
const playerContainer = ref(null)
const featureSection = ref(null)
let player = null

const isPlaying = ref(false)
const showButton = ref(true)
const showThumb = ref(true)
const fadeDelay = 400
let fadeTimeout = null
const timeline = ref(null)

const duration = ref(0)
const currentTime = ref(0)

async function onSeek() {
    if (!player) return

    const ε = 0.05
    const maxT = Math.max(0, duration.value - ε)
    const t = Math.min(Math.max(currentTime.value, 0), maxT)

    try {
        await player.setCurrentTime(t)
    } catch (err) {
        console.warn('seek clamped:', err)
    }
    player.play().catch(() => { })
}
function onTrackClick(event) {
    if (!timeline.value || duration.value <= 0) return

    // 1) Figure out pointer X
    let clientX
    if (event instanceof MouseEvent) {
        clientX = event.clientX
    } else {
        // touch event
        clientX = event.changedTouches[0].clientX
    }

    // 2) Calculate fraction [0–1]
    const rect = timeline.value.getBoundingClientRect()
    let percent = (clientX - rect.left) / rect.width
    percent = Math.min(Math.max(percent, 0), 1)

    // 3) Compute newTime and clamp just under duration
    const rawTime = percent * duration.value
    const epsilon = 0.05
    const maxTime = Math.max(0, duration.value - epsilon)
    const newTime = Math.min(rawTime, maxTime)

    // 4) Apply to model and Vimeo
    currentTime.value = newTime
    player
        .setCurrentTime(newTime)
        .then(() => player.play().catch(() => { }))
        .catch(() => { })
}
const timelineBG = computed(() => {
    const pct = duration.value
        ? (currentTime.value / duration.value) * 100
        : 0
    // first color is the “played” part, second is the “remaining”
    return `linear-gradient(
    to right,
    #F63E02 0%,
    #F63E02 ${pct}%,
    rgba(255,255,255,0.6) ${pct}%,
    rgba(255,255,255,0.6) 100%
  )`
})

const pickBestThumbnail = pics =>
    pics.sizes.reduce((best, s) => (s.width > best.width ? s : best)).link

async function loadVimeo() {
    try {
        const res = await axios.get('https://api.vimeo.com/me/videos', {
            headers: { Authorization: `Bearer ${ACCESS_TOKEN}` },
            params: {
                per_page: 100,
                sort: 'date',
                direction: 'asc',
                filter: 'privacy.view',
                filter_value: 'unlisted'
            }
        })
        const list = res.data.data.map(v => ({
            id: v.uri.split('/').pop(),
            embedUrl: v.player_embed_url,
            thumbnail: pickBestThumbnail(v.pictures),
            title: v.name,
            description: v.description || '',
            tags: v.tags.map(t => t.name)
        }))
        videos.value = list
        selectedVideo.value = list.find(v => v.tags.includes('featured')) || list[0]
    } catch (err) {
        console.error('Vimeo load failed:', err)
    } finally {
        loader.value = false
    }
}

async function initPlayer(video) {
    await nextTick()

    // Ensure the player container element is actually in the DOM
    let el = playerContainer.value
    if (!el) {
        await nextTick()
        el = playerContainer.value
    }
    if (!el) {
        console.warn('Vimeo player container not found; skipping init this tick')
        return
    }

    const opts = {
        url: video.embedUrl,
        width: '100%',
        controls: false,
        autoplay: true,
    }

    if (!player) {
        player = new Player(el, opts)
        player.getDuration().then(d => (duration.value = d))
        player.on('play', () => {
            isPlaying.value = true
            showButton.value = true
            showThumb.value = false
            clearTimeout(fadeTimeout)
            fadeTimeout = setTimeout(() => (showButton.value = false), fadeDelay)
        })
        player.on('pause', () => {
            isPlaying.value = false
            showButton.value = true
            showThumb.value = true
            clearTimeout(fadeTimeout)
        })
        player.on('ended', () => {
            isPlaying.value = false
            showButton.value = true
            showThumb.value = true
            clearTimeout(fadeTimeout)
        })
        player.on('timeupdate', ({ seconds }) => {
            currentTime.value = seconds
        })
    } else {
        await player.loadVideo(video.embedUrl)
    }

    // auto-play on every init
    try {
        await player.play()
    } catch (e) {
        // mobile browsers sometimes block autoplay; user tap will resume
        console.warn('Autoplay blocked:', e)
    }
}

async function selectVideo(vid) {
    // reset UI
    isPlaying.value = false
    showButton.value = true
    showThumb.value = true
    clearTimeout(fadeTimeout)

    selectedVideo.value = vid
    featureSection.value?.scrollIntoView({ behavior: 'smooth' })
    await initPlayer(vid)
}

function onPlayClick() {
    if (!player) return
    isPlaying.value ? player.pause() : player.play()
}

function onWrapperClick() {
    onPlayClick()
}

onMounted(async () => {
    await loadVimeo()
    if (selectedVideo.value) {
        await initPlayer(selectedVideo.value)
    }
})

onBeforeUnmount(() => {
    player?.unload()
    player = null
    clearTimeout(fadeTimeout)
})
</script>

<style scoped>
.page {
    width: 100%;
    max-width: 1240px;
    margin: 0 auto;
    padding: 85px 20px;
    color: #fff;
    text-align: center;
}

.header {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 48px 0 2rem;
}

.avatar {
    width: 86px;
    height: 86px;
    border-radius: 50%;
    object-fit: cover;
}

.tagline {
    margin-top: 24px;
    font-size: 18px;
    font-weight: 300;
}

.loading {
    position: relative;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.loading lottie-player {
    padding-top: 50px;
    margin: 0;
}

/* Video container sized per design specs */
.video-wrapper {
    position: relative;
    width: 840px;
    height: 472px;
    margin: 0 auto 1rem;
    border-radius: 24px;
    overflow: hidden;
}

.featured-player {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 24px;
    overflow: hidden;
    z-index: 0;
}

.video-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
}

/* ensure the injected iframe also fills */
::v-deep .featured-player iframe {
    width: 100% !important;
    height: 100% !important;
}

.custom-play-btn {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
    z-index: 2;
}

.custom-play-btn.visible,
.video-wrapper:hover .custom-play-btn {
    opacity: 1;
    pointer-events: auto;
}

.video-title {
    margin-top: 45px;
    margin-bottom: 0;
    font-size: 24px;
    font-weight: 400px;
}

.video-description {
    margin: 10px auto 64px;
    max-width: 850px;
    font-size: 18px;
    font-weight: 300;
    line-height: 140%;
    color: #444444;
}

.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(236px, 1fr));
    gap: 22px;
}

.grid-item {
    position: relative;
    cursor: pointer;
    border-radius: 8px;
    overflow: hidden;
    transition: transform 0.2s;
}

.grid-item:hover {
    transform: scale(1.03);
}

.grid-item img {
    width: 100%;
    height: auto;
    display: block;
}

.badge {
    position: absolute;
    top: 8px;
    left: 8px;
    background: gold;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75rem;
    color: #111;
}

.timeline-wrapper {
    position: absolute;
    bottom: 12px;
    left: 16px;
    right: 16px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s;
    z-index: 3;
}

.video-wrapper:hover .timeline-wrapper {
    opacity: 1;
    pointer-events: auto;
}

.timeline {
    -webkit-appearance: none;
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 0, 0, 0.6);
    outline: none;
    cursor: pointer;
}

/* webkit thumb */
.timeline::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #F63E02;
    margin-top: 0;
    /* center the thumb on the track */
}

/* Firefox thumb */
.timeline::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #F63E02;
    border: none;
}

/* Responsive fallback */
@media (max-width: 1024px) {
    .video-wrapper {
        width: 100%;
        height: auto;
        aspect-ratio: 16/9;
        border-radius: 16px;
    }
}

@media (max-width: 768px) {
    .avatar {
        width: 86px;
        height: 86px;
    }

    .tagline {
        font-size: 1.125rem;
    }

    .grid {
        grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
        gap: 16px;
    }

    .loading lottie-player {
        padding-top: 50px;
        margin: 0;
    }
}

@media (max-width: 480px) {
    .avatar {
        width: 86px;
        height: 86px;
    }

    .tagline {
        font-size: 1rem;
    }

    .grid {
        grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
        gap: 12px;
    }
}
</style>
